import React, { useState } from "react";
import { ArrowRight, Check, Clock } from "lucide-react";
import { FREE_FEATURES, PREMIUM_PLANNED } from "@/components/landing/landingData";
import { GreenButton } from "@/components/landing/LandingHeader";

const FreeCard = ({ onFree, onSignIn }) => (
  <div data-testid="lp-plan-free" className="relative rounded-3xl border border-[#22D3EE]/50 bg-gradient-to-b from-[#071426] to-[#03050A] p-7 shadow-[0_0_60px_rgba(34,211,238,0.12)] sm:p-9">
    <span className="rounded-full bg-[#22D3EE] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-black">Available now</span>
    <p className="mt-6 font-mono text-xs font-bold uppercase tracking-[0.3em] text-slate-300">Hat Trick Free</p>
    <p data-testid="lp-free-price" className="mt-2 font-condensed text-6xl font-extrabold text-white">$0<span className="ml-1 text-xl font-bold text-slate-400">/ month</span></p>
    <h3 className="mt-4 font-condensed text-2xl font-extrabold uppercase tracking-wide text-[#22D3EE]">The edge starts here.</h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-300">Get inside Hat Trick today. Explore available sports dashboards, research player matchups, and discover the analytics available to your account.</p>
    <ul className="mt-6 space-y-2.5">
      {FREE_FEATURES.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-slate-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#22D3EE]" />{f}</li>)}
    </ul>
    <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-slate-500">No credit card. Data reload tools stay with the admin account.</p>
    <GreenButton data-testid="lp-plan-free-cta" onClick={onFree} className="mt-7 w-full">Get Free Access <ArrowRight className="h-4 w-4" /></GreenButton>
    <p className="mt-4 text-center text-sm text-slate-400">Already have an account? <button data-testid="lp-plan-signin" onClick={onSignIn} className="font-semibold text-[#22D3EE] hover:text-white">Sign In</button></p>
  </div>
);

const PremiumCard = () => {
  const [info, setInfo] = useState(false);
  return (
    <div data-testid="lp-plan-premium" className="relative rounded-3xl border border-[#22D3EE]/20 bg-[#050B16] p-7 shadow-[0_0_40px_rgba(34,211,238,0.05)] sm:p-9">
      <span className="rounded-full border border-[#22D3EE]/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[#22D3EE]">Coming soon</span>
      <p className="mt-6 font-mono text-xs font-bold uppercase tracking-[0.3em] text-slate-300">Hat Trick Premium</p>
      <p data-testid="lp-premium-price" className="mt-2 font-condensed text-5xl font-extrabold uppercase text-white/90">Coming soon</p>
      <h3 className="mt-4 font-condensed text-2xl font-extrabold uppercase tracking-wide text-white">More intelligence. Deeper analytics.</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">Hat Trick Premium is in development. Get ready for expanded research capabilities, deeper analytical breakdowns, and additional tools across supported sports.</p>
      <ul className="mt-6 space-y-2.5">
        {PREMIUM_PLANNED.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#22D3EE]/70" /><span>{f} <span className="ml-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Planned</span></span>
          </li>
        ))}
      </ul>
      <button data-testid="lp-plan-premium-cta" onClick={() => setInfo(true)} aria-expanded={info}
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#22D3EE]/50 px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.18em] text-[#22D3EE] transition-colors hover:bg-[#22D3EE]/10">
        Join the early access list <ArrowRight className="h-4 w-4" />
      </button>
      {info && (
        <p data-testid="lp-premium-info" role="status" className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3 text-center text-[13px] text-slate-300">
          The early access list isn’t open yet. Premium has no price or launch date; nothing has been collected. Create a free account today and you’ll see Premium in the app when it arrives.
        </p>
      )}
    </div>
  );
};

export const Pricing = ({ onFree, onSignIn }) => (
  <section id="pricing" data-testid="lp-pricing" className="scroll-mt-16 border-t border-white/5 py-20">
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#22D3EE]">Pricing</p>
      <h2 className="mt-3 font-condensed text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">Free today. Premium coming soon.</h2>
      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr] lg:items-start">
        <FreeCard onFree={onFree} onSignIn={onSignIn} />
        <PremiumCard />
      </div>
    </div>
  </section>
);
